import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, Eye, Users } from 'lucide-react';
import { DataTable, Column } from '../../components/DataTable';
import { teamApi, userManagementApi } from '../../services/api';
import { Team, User } from '../../types';
import TeamForm from './components/TeamForm';
import TeamDetailsPanel from './components/TeamDetailsPanel';

const TeamsPage: React.FC = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 15,
    total: 0,
    lastPage: 1,
  });
  const [filters, setFilters] = useState({
    search: '',
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [viewingTeam, setViewingTeam] = useState<Team | null>(null);

  const loadTeams = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        page: pagination.currentPage,
        per_page: pagination.perPage,
      };
      if (filters.search) params.search = filters.search;

      const response = await teamApi.list(params);
      setTeams(response.data);
      setPagination((prev) => ({
        ...prev,
        currentPage: response.current_page,
        perPage: response.per_page,
        total: response.total,
        lastPage: response.last_page,
      }));
    } catch (error) {
      console.error('Failed to load teams:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.perPage, filters.search]);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handlePerPageChange = (perPage: number) => {
    setPagination((prev) => ({ ...prev, perPage, currentPage: 1 }));
  };

  const handleSearch = (search: string) => {
    setFilters((prev) => ({ ...prev, search }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleDelete = async (teamId: number) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;
    try {
      await teamApi.delete(teamId);
      loadTeams();
    } catch (error) {
      console.error('Failed to delete team:', error);
      alert('Failed to delete team');
    }
  };

  const columns: Column<Team>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: false,
      render: (team) => (
        <div>
          <div className="font-medium theme-text-primary">{team.name}</div>
          {team.description && (
            <div className="text-sm theme-text-secondary mt-1">{team.description}</div>
          )}
        </div>
      ),
    },
    {
      key: 'members',
      header: 'Members',
      sortable: false,
      render: (team) => (
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 theme-text-tertiary" />
          <span className="text-sm theme-text-secondary">
            {team.members?.length || 0} members
          </span>
        </div>
      ),
    },
    {
      key: 'creator',
      header: 'Created By',
      sortable: false,
      render: (team) => (
        <span className="text-sm theme-card-subtitle">
          {team.creator?.name || 'Unknown'}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      sortable: false,
      render: (team) => (
        <span className="text-sm theme-card-subtitle">
          {team.created_at ? new Date(team.created_at).toLocaleDateString() : '-'}
        </span>
      ),
    },
  ];

  return (
    <div className="theme-card rounded-2xl p-3 md:p-4 relative border theme-border">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-semibold theme-card-title">Teams</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-1.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-3 py-1.5 rounded-lg hover:shadow-lg transition-shadow text-sm"
        >
          <Plus className="w-3.5 h-3.5" />
          Create Team
        </button>
      </div>

      <DataTable
        columns={columns}
        data={teams}
        loading={loading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPerPageChange={handlePerPageChange}
        searchable
        onSearch={handleSearch}
        searchPlaceholder="Search teams by name or description..."
        emptyMessage="No teams found"
        actions={(team) => (
          <div className="flex items-center gap-1.5 justify-end">
            <button
              onClick={() => setViewingTeam(team)}
              className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
              title="View Details"
            >
              <Eye className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setEditingTeam(team)}
              className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
              title="Edit"
            >
              <Edit className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => handleDelete(team.id)}
              className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
              title="Delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      />

      {showCreateModal && (
        <TeamForm
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadTeams();
          }}
        />
      )}

      {editingTeam && (
        <TeamForm
          team={editingTeam}
          onClose={() => setEditingTeam(null)}
          onSuccess={() => {
            setEditingTeam(null);
            loadTeams();
          }}
        />
      )}

      {viewingTeam && (
        <TeamDetailsPanel
          teamId={viewingTeam.id}
          onClose={() => setViewingTeam(null)}
          onUpdate={loadTeams}
        />
      )}
    </div>
  );
};

export default TeamsPage;

